import { useRef, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import {
  FilterArgs,
  FilterVote,
  MOST_RELEVANT_AI_SORT,
  NO_FILTER_ARGS,
  SearchFields,
  SortAndFilter,
  SortAndFilterArgs,
  SortValue,
} from '../../types';
import { useUserPreferences } from 'hooks/user/useUserPreferences';

export type DisplayValue = '25' | '50' | '100';

export type FilterForm = {
  keywords: string[];
  selectedTags: string[];
  within: SearchFields;
  has_full_text?: boolean;
  needs_full_text?: boolean;
  vote?: FilterVote | 'All';
};

// The toolbar's one form: filters plus the sort and display choices
export type FilterSortDisplayForm = {
  sort: SortValue;
  display: DisplayValue;
  filter: FilterForm;
};

type UseFilterSortDisplayFormOptions = {
  allowAISort: boolean;
  sortAndFilter: SortAndFilter;
};

type UseFilterSortDisplayFormReturn = {
  form: UseFormReturn<FilterSortDisplayForm>;
  sortOptions: Record<string, string>;
  isResetting: boolean;
  numStudiesFound: number;
  appliedFilterCount: number;
  applySortAndFilter: (values: FilterSortDisplayForm) => Promise<void>;
  resetFiltersAndRefresh: () => Promise<void>;
};

const filterDefaults: FilterForm = {
  ...NO_FILTER_ARGS,
  selectedTags: [],
  vote: 'All',
};

// Checkboxes are absent from the defaults, so name them or reset leaves them ticked
const clearedFilter: FilterForm = {
  ...filterDefaults,
  has_full_text: false,
  needs_full_text: false,
};

export const convertFormToFilterArgs = ({
  keywords,
  selectedTags,
  within,
  has_full_text,
  needs_full_text,
  vote,
}: FilterForm): FilterArgs => {
  return {
    keywords: [...keywords, ...selectedTags], // The filter endpoint expects just one list of keywords, whereas the UI separates keywords/tags
    within,
    ...(has_full_text !== needs_full_text && { has_full_text }), // Both checkboxes checked or both checkboxes unchecked means "everything", so don't submit the param in this case
    ...(vote !== 'All' && { vote }), // Don't submit vote param if 'All' votes is selected, as it means any voting status should be returned
  };
};

export const countActiveFilters = ({
  keywords,
  selectedTags,
  within,
  has_full_text,
  needs_full_text,
  vote,
}: FilterForm): number => {
  let numFiltersActive = keywords.length + selectedTags.length;
  if (within !== 'title_and_abstract') numFiltersActive++;
  if (has_full_text || needs_full_text) numFiltersActive++;
  if (vote !== 'All') numFiltersActive++;
  return numFiltersActive;
};

const convertFormToSortAndFilterArgs = ({
  sort,
  display,
  filter,
}: FilterSortDisplayForm): SortAndFilterArgs => ({
  sort,
  display: parseInt(display),
  filter: convertFormToFilterArgs(filter),
});

export const useFilterSortDisplayForm = ({
  allowAISort,
  sortAndFilter,
}: UseFilterSortDisplayFormOptions): UseFilterSortDisplayFormReturn => {
  const {
    studyListPreferences,
    updateStudyListPreferences,
  } = useUserPreferences();
  const relevancySort = allowAISort ? MOST_RELEVANT_AI_SORT : 'relevancy';
  const sortOptions = {
    author: 'Author',
    title: 'Title',
    covidence_number: 'Covidence ID',
    most_recent: 'Most recent',
    [relevancySort]: 'Most relevant',
  };

  // Mirrors the server, which saves sort_by/per_page from every list request and swaps
  // 'relevancy'/'most_relevant_ai' to suit the review (Reviews::StudiesController)
  const savedSort = studyListPreferences.sort_by;
  const defaultSortAndDisplay: Pick<
    FilterSortDisplayForm,
    'sort' | 'display'
  > = {
    sort: !savedSort || savedSort === 'relevancy' ? relevancySort : savedSort,
    display: studyListPreferences.per_page || '25',
  };
  const form = useForm<FilterSortDisplayForm>({
    defaultValues: { ...defaultSortAndDisplay, filter: filterDefaults },
  });
  const { getValues, reset } = form;
  // What the list is sorted by, as opposed to the form's possibly unapplied choice
  const appliedSortAndDisplay = useRef(defaultSortAndDisplay);
  const [numStudiesFound, setNumStudiesFound] = useState(0);
  const [appliedFilterCount, setAppliedFilterCount] = useState(0);
  const [isResetting, setIsResetting] = useState(false);

  const applySortAndFilter = async (values: FilterSortDisplayForm) => {
    const studiesFound = await sortAndFilter(
      convertFormToSortAndFilterArgs(values)
    );
    appliedSortAndDisplay.current = {
      sort: values.sort,
      display: values.display,
    };
    // Cookie after the fetch, so a failed refresh never claims a sort that didn't happen
    updateStudyListPreferences({
      sort_by:
        values.sort === MOST_RELEVANT_AI_SORT ? 'relevancy' : values.sort,
      per_page: values.display,
    });
    setNumStudiesFound(studiesFound);
    setAppliedFilterCount(countActiveFilters(values.filter));
  };

  // Sort and display are preferences, not filters, so the list keeps its applied ones
  const resetFiltersAndRefresh = async () => {
    // Not via handleSubmit: that would flip isSubmitting and light up the Apply button too
    setIsResetting(true);
    try {
      await applySortAndFilter({
        ...appliedSortAndDisplay.current,
        filter: clearedFilter,
      });
      // Only once the list has refreshed, so a failed reset leaves the form matching it
      reset({ ...getValues(), filter: clearedFilter });
    } finally {
      setIsResetting(false);
    }
  };

  return {
    form,
    sortOptions,
    isResetting,
    numStudiesFound,
    appliedFilterCount,
    applySortAndFilter,
    resetFiltersAndRefresh,
  };
};
